/* Use the existing Flickity instance so navigation and video lifecycle stay together. */
(() => {
  const attached = new WeakSet();
  const mediaRoots = new WeakMap();
  function connect(root) {
    const main = root.querySelector('.slideshow-outer');
    if (!main) return null;
    const carousel = window.Flickity?.data(main);
    root.dataset.bestekHeroNavigation = carousel ? 'ready' : (window.Flickity ? 'waiting' : 'library-unavailable');
    if (!carousel) return null;
    if (!attached.has(main)) {
      attached.add(main);
      const update = () => root.querySelectorAll('[data-bestek-hero-index]').forEach(button => {
        button.setAttribute('aria-pressed', String(Number(button.dataset.bestekHeroIndex) === carousel.selectedIndex));
      });
      carousel.on('select', () => { update(); mediaRoots.get(root)?.sync(); });
      update();
    }
    return carousel;
  }
  function connectMedia(root) {
    if (mediaRoots.has(root)) return;
    const main = root.querySelector('.slideshow-outer');
    const hosts = Array.from(root.querySelectorAll('[data-bestek-banner-video]'));
    if (!main || !hosts.length) return;
    const reduced = matchMedia('(prefers-reduced-motion: reduce)');
    const narrow = matchMedia('(max-width: 767px)');
    const events = new AbortController();
    const states = hosts.map(host => ({
      host, video: host.querySelector('video'), button: host.querySelector('[data-bestek-video-toggle]'),
      status: host.querySelector('[data-bestek-video-status]'), manual: false, pausedByUser: false,
      blocked: false, failed: false, pending: false, generation: 0, abortRecoveryUsed: false
    })).filter(state => state.video);
    let inView = false;
    let disposed = false;
    let frame = 0;
    const currentSlide = () => {
      const carousel = window.Flickity?.data(main);
      return carousel?.selectedElements?.[0] || main.querySelector('.slideshow-item.is-selected') || main.querySelector('.slideshow-item');
    };
    const allowed = state => !disposed && inView && !document.hidden && !state.pausedByUser && (!reduced.matches || state.manual) &&
      state.host.closest('.slideshow-item') === currentSlide();
    const updateButton = state => {
      if (disposed || !state.button) return;
      const playing = !state.video.paused && !state.video.ended && !state.failed;
      state.button.hidden = playing;
      state.button.textContent = state.failed ? 'Retry video' : playing ? 'Pause video' : 'Play video';
      state.button.setAttribute('aria-label', state.button.textContent);
    };
    const showPoster = state => state.host.removeAttribute('data-playing-frame');
    const sourceFor = video => {
      const connection = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const smallSource = narrow.matches || connection?.saveData || /(^|-)2g$/.test(connection?.effectiveType || '');
      return (smallSource ? video.dataset.bestekMobileSrc : video.dataset.bestekDesktopSrc) || video.dataset.bestekDesktopSrc;
    };
    const ensureSource = state => {
      if (state.video.getAttribute('src')) return true;
      const source = sourceFor(state.video);
      if (!source) return false;
      state.video.defaultMuted = true;
      state.video.muted = true;
      state.video.playsInline = true;
      state.video.autoplay = true;
      state.video.src = source;
      state.video.load();
      return true;
    };
    const play = state => {
      if (!allowed(state) || state.pending || state.failed || state.blocked) return;
      if (!ensureSource(state)) { state.failed = true; updateButton(state); return; }
      if (!state.video.paused) return;
      state.pending = true;
      const generation = ++state.generation;
      let retryAbortedPlayback = false;
      const request = state.video.play();
      Promise.resolve(request).then(() => {
        if (disposed) {
          // Stop a late fulfillment on an unloaded node, but never pause a
          // replacement controller if the editor has reinserted the same DOM.
          if (!mediaRoots.has(root)) state.video.pause();
          return;
        }
        if (generation !== state.generation) return;
        if (!allowed(state)) state.video.pause();
      }).catch(error => {
        if (disposed || generation !== state.generation) return;
        if (error?.name === 'AbortError') {
          // A slide can return while its interrupted play() is still settling.
          // Allow one recovery per visible playback, never an automatic loop.
          retryAbortedPlayback = allowed(state) && !state.abortRecoveryUsed;
          if (retryAbortedPlayback) state.abortRecoveryUsed = true;
          return;
        }
        state.blocked = true;
        showPoster(state);
        if (state.status) state.status.textContent = 'Video did not start automatically. Select Play video to watch.';
      }).finally(() => {
        if (disposed || generation !== state.generation) return;
        state.pending = false;
        updateButton(state);
        if (retryAbortedPlayback && allowed(state) && !state.blocked && !state.failed) scheduleSync();
      });
    };
    const sync = () => {
      states.forEach(state => {
        if (!allowed(state)) {
          state.abortRecoveryUsed = false;
          state.video.pause();
          if (reduced.matches && !state.manual) showPoster(state);
        } else play(state);
        updateButton(state);
      });
    };
    const scheduleSync = () => {
      if (frame) return;
      frame = requestAnimationFrame(() => { frame = 0; sync(); });
    };
    states.forEach(state => {
      const signal = events.signal;
      // Place the control above the content/overlay stacking layer, not inside the background image.
      if (state.button) state.host.closest('.slideshow-item')?.appendChild(state.button);
      state.video.addEventListener('playing', () => {
        if (!allowed(state)) { state.video.pause(); return; }
        state.host.setAttribute('data-playing-frame', '');
        state.blocked = false;
        state.abortRecoveryUsed = false;
        if (state.status) state.status.textContent = '';
        updateButton(state);
      }, {signal});
      state.video.addEventListener('pause', () => { showPoster(state); updateButton(state); }, {signal});
      state.video.addEventListener('canplay', () => play(state), {signal});
      state.video.addEventListener('error', () => {
        state.failed = true;
        state.pending = false;
        state.video.pause();
        showPoster(state);
        if (state.status) state.status.textContent = 'Video could not load. Select Retry video to try again.';
        updateButton(state);
      }, {signal});
      state.button?.addEventListener('click', () => {
        if (!state.video.paused && !state.failed) {
          state.pausedByUser = true;
          state.manual = false;
          state.video.pause();
          updateButton(state);
          return;
        }
        state.manual = true;
        state.pausedByUser = false;
        state.blocked = false;
        state.abortRecoveryUsed = false;
        const retry = state.failed;
        state.failed = false;
        state.pending = false;
        state.generation++;
        if (retry) state.video.load();
        play(state);
      }, {signal});
      updateButton(state);
    });
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      inView = entries[0].isIntersecting;
      sync();
    }, {threshold: 0.02}) : null;
    if (observer) observer.observe(root);
    else { inView = true; sync(); }
    const mutation = new MutationObserver(scheduleSync);
    mutation.observe(main, {attributes: true, subtree: true, attributeFilter: ['class']});
    document.addEventListener('visibilitychange', sync, {signal: events.signal});
    const motionChanged = () => { states.forEach(state => { state.manual = false; }); sync(); };
    reduced.addEventListener?.('change', motionChanged);
    mediaRoots.set(root, {sync, cleanup: () => {
      disposed = true;
      inView = false;
      cancelAnimationFrame(frame);
      frame = 0;
      states.forEach(state => { state.generation++; state.pending = false; });
      observer?.disconnect();
      mutation.disconnect();
      events.abort();
      states.forEach(state => state.video.pause());
      reduced.removeEventListener?.('change', motionChanged);
      mediaRoots.delete(root);
    }});
  }
  const connectRoot = root => { connectMedia(root); connect(root); };
  function start() {
    document.querySelectorAll('.bestek-home-hero').forEach(connectRoot);
    window.addEventListener('load', () => document.querySelectorAll('.bestek-home-hero').forEach(connectRoot), {once:true});
    document.addEventListener('click', event => {
      const button = event.target.closest('[data-bestek-hero-index]');
      if (!button) return;
      const root = button.closest('.bestek-home-hero');
      const carousel = root && connect(root);
      if (!carousel) return;
      event.preventDefault();
      carousel.selectCell(Number(button.dataset.bestekHeroIndex));
    });
    document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('.bestek-home-hero').forEach(connectRoot));
    document.addEventListener('shopify:section:unload', event => event.target.querySelectorAll('.bestek-home-hero').forEach(root => mediaRoots.get(root)?.cleanup()));
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', start, {once:true});
  else start();
})();
